import React from "react";
import { Link } from "react-router-dom";

import { Badge } from "src/components/ui/Badge";
import Button from "src/components/ui/Button";
import { formatDateTime } from "src/utils/date-time";
import { currencyFormatter } from "src/utils/common";
import { Tenant, TenantPlanSnapshot } from "src/types/tenant.types";

interface TenantPlanDetailsProps {
  tenantDetails: Tenant | null;
  activePlan: TenantPlanSnapshot | null;
  onExtendPlan: () => void;
  getPlanStatus: (planEndsAt?: string) => string;
}

const TenantPlanDetails: React.FC<TenantPlanDetailsProps> = ({
  tenantDetails,
  activePlan,
  onExtendPlan,
  getPlanStatus,
}) => {
  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between">
          <h4 className="text-base font-semibold text-gray-800">Active Plan</h4>
          <Button variant="outline" onClick={onExtendPlan}>
            Extend Plan
          </Button>
        </div>
        <div className="mt-3 rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-700">
          {activePlan ? (
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <div className="text-gray-500">Plan Name</div>
                <div className="font-semibold text-gray-900">
                  {activePlan?.planName || "-"}
                </div>
              </div>
              <div>
                <div className="text-gray-500">Plan Code</div>
                <div className="font-semibold text-gray-900">
                  {activePlan?.planCode || activePlan?.planId || "-"}
                </div>
              </div>
              <div>
                <div className="text-gray-500">Price</div>
                <div className="font-semibold text-gray-900">
                  {activePlan?.price !== undefined
                    ? currencyFormatter(activePlan?.price)
                    : "-"}
                </div>
              </div>
              <div>
                <div className="text-gray-500">Duration</div>
                <div className="font-semibold text-gray-900">
                  {activePlan?.durationInDays
                    ? `${activePlan?.durationInDays} days`
                    : "-"}
                </div>
              </div>
              <div>
                <div className="text-gray-500">Start Date</div>
                <div className="font-semibold text-gray-900">
                  {activePlan?.planStartsAt
                    ? formatDateTime(activePlan?.planStartsAt)
                    : "-"}
                </div>
              </div>
              <div>
                <div className="text-gray-500">End Date</div>
                <div className="font-semibold text-gray-900">
                  {activePlan?.planEndsAt
                    ? formatDateTime(activePlan?.planEndsAt)
                    : "-"}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-gray-500">No active plan assigned.</div>
          )}
        </div>
      </div>

      <div>
        <h4 className="text-base font-semibold text-gray-800">Plan History</h4>
        <div className="mt-3 space-y-3">
          {tenantDetails?.plans?.length ? (
            tenantDetails?.plans.map((plan, index) => (
              <div
                key={`${plan.planId || plan.planCode}-${index}`}
                className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-700"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="font-semibold text-gray-900">
                    {plan.planName || "-"}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500">
                      {plan.planCode || plan.planId || "-"}
                    </span>
                    <Badge
                      text={getPlanStatus(plan.planEndsAt)}
                      color={
                        getPlanStatus(plan.planEndsAt) === "active"
                          ? "green"
                          : "red"
                      }
                    />
                  </div>
                </div>
                <div className="mt-2 grid gap-2 md:grid-cols-3">
                  <div>
                    <div className="text-gray-500">Transaction ID</div>
                    <div className="font-medium text-gray-900 underline">
                      {plan.transactionId ? (
                        <Link to={`/admin/transaction/${plan.transactionId}/details`}>
                          {`#${plan.transactionId}`}
                        </Link>
                      ) : (
                        "-"
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500">Start</div>
                    <div className="font-medium text-gray-900">
                      {plan.planStartsAt
                        ? formatDateTime(plan.planStartsAt)
                        : "-"}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500">End</div>
                    <div className="font-medium text-gray-900">
                      {plan.planEndsAt
                        ? formatDateTime(plan.planEndsAt)
                        : "-"}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500">Amount</div>
                    <div className="font-medium text-gray-900">
                      {plan.price !== undefined
                        ? currencyFormatter(plan.price)
                        : "-"}
                    </div>
                  </div>
                </div>
                <div className="mt-2">
                  <div className="text-gray-500">Remarks:</div>
                  <div className="font-medium text-gray-900">
                    {plan?.userRemarks || "NA"}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="mt-3 rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-700">
              <div className="text-gray-500">No plan history available.</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TenantPlanDetails;
